// Business Dashboard → Compliance → Legal Readiness.
//
// All counts, statuses and next actions come from the backend readiness
// engine. The page never shows a legal percentage: completion progress and
// verified readiness are separate counts, and a critical legal block is shown
// above both.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle, Building2, Car, ClipboardCheck, FileText, History as HistoryIcon, Landmark, Lock,
  MapPin, RefreshCw, Shield, Truck, Users, Wrench,
} from "lucide-react";

import {
  CATEGORY_TABS, errorMessage, hasCap, humanise, lrGet, lrPost, lrPut, statusLabel,
} from "../lib/clientLegal";
import {
  Button, Empty, Field, Loading, Modal, Note, Section, Stat, Tabs, TonePill, inputClass,
} from "../components/platform-legal/ui";
import RequirementDetail, { StatusPill } from "../components/client-legal/RequirementDetail";
import VehicleLegalPanel from "../components/client-legal/VehicleLegalPanel";

const TABS = [
  { key: "overview", label: "Overview", icon: ClipboardCheck },
  { key: "setup", label: "Setup", icon: Wrench },
  { key: "entity", label: "Business", icon: Building2 },
  { key: "local", label: "Local permits", icon: MapPin },
  { key: "bir", label: "BIR & accounting", icon: Landmark },
  { key: "transport", label: "Transport authority", icon: Truck },
  { key: "fleet", label: "Fleet", icon: Car },
  { key: "insurance", label: "Insurance", icon: Shield },
  { key: "privacy", label: "Privacy", icon: Lock },
  { key: "employment", label: "Employment", icon: Users },
  { key: "locations", label: "Locations", icon: MapPin },
  { key: "recurring", label: "Recurring", icon: RefreshCw },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "history", label: "History", icon: HistoryIcon },
];

export default function ComplianceLegalReadiness() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);

  const load = useCallback(() => lrGet("/overview")
    .then((d) => { setData(d); setError(null); })
    .catch((err) => setError(err?.response?.status === 403
      ? "You don't have access to Legal Readiness. Ask the business owner to grant it."
      : errorMessage(err))), []);

  useEffect(() => {
    load();
    lrGet("/options").then(setOptions).catch(() => {});
  }, [load]);

  const caps = useMemo(() => data?.capabilities || [], [data]);
  const tab = params.get("tab") || "overview";
  const visibleTabs = useMemo(() => TABS.filter((t) => {
    if (!data) return t.key === "overview";
    if (hasCap(caps, "compliance.view")) return true;
    return ["overview", "bir"].includes(t.key); // accountant scope
  }), [data, caps]);

  if (error) {
    return (
      <div className="space-y-4" data-testid="legal-readiness-page">
        <Header />
        <Section><Empty testId="cl-no-access">{error}</Empty></Section>
      </div>
    );
  }
  if (!data) return <Loading label="Evaluating legal readiness..." />;

  const openRequirement = (key, scopeId) => setOpen({ key, scopeId });

  return (
    <div className="space-y-4" data-testid="legal-readiness-page">
      <Header />
      <Tabs tabs={visibleTabs} active={tab} onChange={(key) => setParams({ tab: key })} testId="cl-tabs" />
      <div key={tab}>
        {tab === "overview" && <Overview data={data} onOpen={openRequirement} onTab={(k) => setParams({ tab: k })} />}
        {tab === "setup" && <Setup data={data} options={options} caps={caps} onSaved={load} />}
        {CATEGORY_TABS.some((c) => c.key === tab) && (
          <CategoryTab data={data} category={tab} onOpen={openRequirement} />
        )}
        {tab === "fleet" && <Fleet data={data} options={options} />}
        {tab === "insurance" && <Insurance data={data} options={options} />}
        {tab === "locations" && <Locations data={data} options={options} caps={caps} onSaved={load} onOpen={openRequirement} />}
        {tab === "recurring" && <Recurring data={data} onOpen={openRequirement} />}
        {tab === "documents" && <Documents caps={caps} />}
        {tab === "history" && <HistoryTab />}
      </div>
      <Note tone="slate" testId="cl-disclaimer">{data.disclaimer}</Note>
      {open && (
        <RequirementDetail requirementKey={open.key} scopeId={open.scopeId === "business" ? null : open.scopeId}
                           options={options} onClose={() => setOpen(null)} onChanged={load} />
      )}
    </div>
  );
}

function Header() {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Compliance</div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1">Legal Readiness</h1>
      <p className="text-sm text-slate-600 mt-1 max-w-3xl">
        Understand, document and maintain the registrations and requirements that may apply to your
        rental business and fleet.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
function Overview({ data, onOpen, onTab }) {
  const gate = data.legal_gate;
  return (
    <div className="space-y-4">
      {data.missing_facts?.length > 0 && (
        <Note tone="amber" testId="cl-facts-required">
          Answer a few setup questions so the right requirements can be shown.{" "}
          <button type="button" className="underline" onClick={() => onTab("setup")}>Start setup</button>
        </Note>
      )}
      {gate.critical_blocks.length > 0 && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="cl-critical-blocks">
          <div className="flex items-center gap-2 text-rose-800 font-semibold">
            <AlertTriangle className="w-4 h-4" /> Critical legal block
          </div>
          <ul className="mt-1 text-sm text-rose-900 list-disc pl-5">
            {gate.critical_blocks.map((b) => (
              <li key={b.requirement_key + b.scope_id}>{b.title}{b.service ? ` (${humanise(b.service)} service)` : ""}: {b.next_action}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="cl-business-status">
          <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">Business legal status</div>
          <div className="mt-1"><StatusPill status={data.business_status} /></div>
          <div className="mt-1 text-xs text-slate-500">Legal gate: {statusLabel(gate.gate)}</div>
        </div>
        <Stat label="Checklist addressed" value={`${data.completion_progress.addressed} / ${data.completion_progress.applicable}`}
              hint="Completion progress, not a legal score" testId="cl-completion" />
        <Stat label="Verified by ConfirmPH" value={`${data.verified_readiness.verified} / ${data.verified_readiness.applicable}`}
              hint="Applicable requirements with reviewed evidence" testId="cl-verified" />
        <Stat label="Awaiting ConfirmPH review" value={data.evidence_pending_review} testId="cl-pending" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cl-fleet-cards">
        <Stat label="Vehicles ready for rental" value={`${data.fleet.ready_for_rental} / ${data.fleet.total}`} />
        <Stat label="Vehicles legally blocked" value={data.fleet.legally_blocked}
              tone={data.fleet.legally_blocked ? "text-rose-700" : "text-slate-900"} />
        <Stat label="Safety holds" value={data.fleet.safety_hold} />
        <Stat label="Expiring within 30 days" value={data.fleet.expiring_soon} />
      </div>
      {data.driver_legal_system.required && (
        <Note tone="sky" testId="cl-driver-system">
          <span className="font-semibold">Driver readiness.</span> {data.driver_legal_system.explanation}{" "}
          <a href="/app/drivers" className="underline font-medium">Open Drivers</a>
        </Note>
      )}
      {gate.authority_review.length > 0 && (
        <Section title="Agency review recommended">
          <ul className="text-sm text-slate-700 list-disc pl-5">
            {gate.authority_review.map((a) => <li key={a.requirement_key}>{a.title}: {a.explanation}</li>)}
          </ul>
        </Section>
      )}
      <Section title="What should I do next?" testId="cl-next-actions">
        {data.next_actions.length === 0 ? <Empty>No actions are waiting on you right now.</Empty> : (
          <ol className="space-y-2">
            {data.next_actions.map((a, i) => (
              <li key={`${a.requirement_key}-${a.scope_id}-${a.vehicle_id}-${i}`} className="rounded-xl border border-slate-200 p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-slate-900">{i + 1}. {a.title}</span>
                  {a.status && <StatusPill status={a.status} />}
                </div>
                {a.why && <p className="mt-1 text-xs text-slate-700"><span className="font-semibold">Why:</span> {a.why}</p>}
                {a.where && <p className="text-xs text-slate-700"><span className="font-semibold">Where:</span> {a.where}</p>}
                {a.prepare?.length > 0 && <p className="text-xs text-slate-700"><span className="font-semibold">Prepare:</span> {a.prepare.join("; ")}</p>}
                {a.expected_cost && <p className="text-xs text-slate-600"><span className="font-semibold">Cost:</span> {a.expected_cost}</p>}
                {a.after?.length > 0 && <p className="text-xs text-slate-700"><span className="font-semibold">After:</span> upload {a.after.join("; ")}</p>}
                {a.waiting_on?.length > 0 && <p className="text-xs text-amber-800">Complete first: {a.waiting_on.join(", ")}</p>}
                {a.requirement_key && a.kind === "requirement" && (
                  <button type="button" className="mt-1 text-xs underline" onClick={() => onOpen(a.requirement_key, a.scope_id)}>
                    View guide
                  </button>
                )}
                {a.kind === "vehicle" && (
                  <Link to={`/app/vehicles/${a.vehicle_id}?tab=legal`} className="mt-1 inline-block text-xs underline">Open vehicle</Link>
                )}
              </li>
            ))}
          </ol>
        )}
      </Section>
      <Section title="Upcoming expiries" testId="cl-expiries">
        {data.upcoming_expiries.length === 0 ? <Empty>No recorded expiries in the next 60 days.</Empty> : (
          <ul className="space-y-1 text-sm">
            {data.upcoming_expiries.map((e) => (
              <li key={`${e.requirement_key}-${e.vehicle_id || e.scope_id}-${e.date}`} className="flex flex-wrap justify-between gap-2">
                <span>{e.title}</span>
                <span className="text-xs text-slate-600">{e.days < 0 ? `expired ${-e.days} days ago` : `in ${e.days} days`} · {e.date}
                  {e.confirmed ? "" : " (not reviewed)"}</span>
              </li>
            ))}
          </ul>
        )}
      </Section>
      {data.public_preview && (
        <Section title="What renters see" description="A sanitised summary only. No documents or reference numbers are ever public.">
          <ul className="space-y-1 text-sm text-slate-700" data-testid="cl-public-preview">
            {data.public_preview.items.map((i) => <li key={i.label}><span className="font-semibold">{i.label}:</span> {i.text}</li>)}
            <li>{data.public_preview.overall.text}</li>
          </ul>
        </Section>
      )}
    </div>
  );
}

function RequirementList({ rows, onOpen, testId }) {
  if (!rows.length) return <Empty>No requirements in this section apply to your recorded operations.</Empty>;
  return (
    <ul className="space-y-2" data-testid={testId}>
      {rows.map((r) => (
        <li key={`${r.key}-${r.scope_id}`}>
          <button type="button" onClick={() => onOpen(r.key, r.scope_id)}
                  className="w-full text-left rounded-xl border border-slate-200 bg-white p-3 hover:bg-slate-50"
                  data-testid={`cl-req-${r.key}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="text-sm font-medium text-slate-900">{r.title}</span>
              <span className="flex flex-wrap gap-1">
                {r.criticality === "CRITICAL_BLOCKER" && (
                  <TonePill tone="border-slate-300 bg-slate-100 text-slate-700">Critical</TonePill>
                )}
                <StatusPill status={r.status} />
              </span>
            </div>
            <p className="mt-0.5 text-xs text-slate-600">{r.applicable ? r.short_description : r.applicability_reason}</p>
          </button>
        </li>
      ))}
    </ul>
  );
}

function CategoryTab({ data, category, onOpen }) {
  const [showNa, setShowNa] = useState(false);
  const categories = category === "entity" ? ["entity", "corporate", "operations", "special"] : [category];
  const rows = data.requirements.filter((r) => categories.includes(r.category) && (showNa || r.applicable || r.status === "FACTS_REQUIRED"));
  const locationRows = data.locations.flatMap((l) => l.requirements
    .filter((r) => r.category === category && (showNa || r.applicable))
    .map((r) => ({ ...r, title: `${r.title} — ${l.location.name}` })));
  const label = TABS.find((t) => t.key === category)?.label;
  return (
    <Section title={label} actions={
      <label className="text-xs flex items-center gap-1">
        <input type="checkbox" checked={showNa} onChange={(e) => setShowNa(e.target.checked)} /> Show not applicable
      </label>}>
      {category === "bir" && (
        <Note tone="slate">ConfirmPH Finance &amp; Books keeps Internal Accounting Records / Accountant-Ready
          Records. It is not BIR-registered computerised books unless your business registered it.</Note>
      )}
      {category === "transport" && (
        <Note tone="slate">Transport authority depends on how you actually operate, never on vehicle type alone.</Note>
      )}
      <div className="mt-3"><RequirementList rows={[...rows, ...locationRows]} onOpen={onOpen} testId={`cl-list-${category}`} /></div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
function Setup({ data, options, caps, onSaved }) {
  const facts = data.facts;
  const [form, setForm] = useState({
    entity_type: facts.entity_type || "", operating_status: facts.operating_status || "",
    rental_modes: facts.rental_modes || [], customer_types: facts.customer_types || [],
    facility_activities: facts.facility_activities || [], telematics: facts.telematics || [],
    vehicle_ownership_mix: facts.vehicle_ownership_mix || [],
    has_employees: facts.has_employees, supplies_drivers: facts.supplies_drivers,
    has_foreign_workers: facts.has_foreign_workers,
  });
  const [changes, setChanges] = useState(null);
  const [busy, setBusy] = useState(false);
  const canEdit = hasCap(caps, "compliance.manage");
  if (!options) return <Loading />;

  const toggle = (field, value) => setForm({ ...form, [field]: form[field].includes(value)
    ? form[field].filter((v) => v !== value) : [...form[field], value] });
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k]; });
      const result = await lrPut("/profile", payload);
      setChanges(result.changes);
      toast.success("Saved. Requirements re-evaluated.");
      onSaved();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const somethingChanged = async (change) => {
    setBusy(true);
    try {
      const result = await lrPost("/profile/change", { change });
      setChanges(result.changes);
      toast.success("Requirements re-evaluated.");
      onSaved();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const Multi = ({ field, values, label }) => (
    <fieldset>
      <legend className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</legend>
      <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
        {values.map((v) => (
          <label key={v} className="flex items-center gap-2 text-sm">
            <input type="checkbox" disabled={!canEdit} checked={form[field].includes(v)} onChange={() => toggle(field, v)}
                   data-testid={`cl-setup-${field}-${v}`} />
            {humanise(v)}
          </label>
        ))}
      </div>
    </fieldset>
  );
  const YesNo = ({ field, label }) => (
    <Field label={label}>
      <select className={inputClass} disabled={!canEdit} value={form[field] === true ? "yes" : form[field] === false ? "no" : ""}
              onChange={(e) => setForm({ ...form, [field]: e.target.value === "" ? null : e.target.value === "yes" })}
              data-testid={`cl-setup-${field}`}>
        <option value="">Not answered</option><option value="yes">Yes</option><option value="no">No</option>
      </select>
    </Field>
  );

  return (
    <div className="space-y-4">
      <Section title="Legal readiness setup" description="Only facts ConfirmPH doesn't already know. Your answers decide which requirements apply."
               testId="cl-setup">
        {facts.derived_driver_services_enabled && (
          <Note tone="sky">Driver services are switched on in your settings, so with-driver requirements apply.</Note>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Legal entity">
            <select className={inputClass} disabled={!canEdit} value={form.entity_type}
                    onChange={(e) => setForm({ ...form, entity_type: e.target.value })} data-testid="cl-setup-entity">
              <option value="">Not answered</option>
              {options.entity_types.map((o) => <option key={o} value={o}>{humanise(o)}</option>)}
            </select>
          </Field>
          <Field label="Operating status">
            <select className={inputClass} disabled={!canEdit} value={form.operating_status}
                    onChange={(e) => setForm({ ...form, operating_status: e.target.value })}>
              <option value="">Not answered</option>
              {options.operating_statuses.map((o) => <option key={o} value={o}>{humanise(o)}</option>)}
            </select>
          </Field>
          <YesNo field="has_employees" label="Employees" />
          <YesNo field="supplies_drivers" label="Company drivers supplied" />
          <YesNo field="has_foreign_workers" label="Foreign workers" />
        </div>
        <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Multi field="rental_modes" values={options.rental_modes} label="How you rent" />
          <Multi field="customer_types" values={options.customer_types} label="Customers" />
          <Multi field="facility_activities" values={options.facility_activities} label="Facility activities" />
          <Multi field="telematics" values={options.telematics} label="GPS / cameras" />
          <Multi field="vehicle_ownership_mix" values={options.ownership_mix} label="Vehicle ownership" />
        </div>
        {canEdit && <div className="mt-3 flex justify-end"><Button busy={busy} onClick={save} data-testid="cl-setup-save">Save and re-evaluate</Button></div>}
      </Section>
      {canEdit && (
        <Section title="Something changed?" description="Tell us what changed and the requirements are re-evaluated.">
          <div className="flex flex-wrap gap-2" data-testid="cl-changes">
            {options.changes.map((c) => (
              <Button key={c.key} variant="secondary" busy={busy} onClick={() => somethingChanged(c.key)}>{c.label}</Button>
            ))}
          </div>
        </Section>
      )}
      {changes && (
        <Section title="What changed" testId="cl-change-result">
          <p className="text-sm">New requirements: {changes.new_requirements.map((c) => c.title).join(", ") || "none"}</p>
          <p className="text-sm">No longer applicable: {changes.no_longer_applicable.map((c) => c.title).join(", ") || "none"}</p>
          <p className="text-sm">Needs review: {changes.needs_review.map((c) => c.title).join(", ") || "none"}</p>
        </Section>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function FleetTable({ vehicles, onOpen, showInsurance = true }) {
  if (!vehicles.length) return <Empty>No vehicles yet.</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" style={{ minWidth: 640 }} data-testid="cl-fleet-table">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-slate-500 border-b">
            <th className="py-2 pr-3">Vehicle</th><th className="py-2 pr-3">Operational</th><th className="py-2 pr-3">Legal</th>
            {showInsurance && <th className="py-2 pr-3">Insurance</th>}<th className="py-2 pr-3">Safety</th>
            <th className="py-2 pr-3">Next expiry</th><th className="py-2 pr-3">Rental</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {vehicles.map((v) => (
            <tr key={v.vehicle_id}>
              <td className="py-2 pr-3"><button type="button" className="underline text-left" onClick={() => onOpen(v.vehicle_id)}>{v.name}</button>
                <div className="text-xs text-slate-500">{v.plate_masked}</div></td>
              <td className="py-2 pr-3 text-xs">{v.operational_status}</td>
              <td className="py-2 pr-3"><StatusPill status={v.legal_status} /></td>
              {showInsurance && <td className="py-2 pr-3">{v.insurance_status ? <StatusPill status={v.insurance_status} /> : "—"}</td>}
              <td className="py-2 pr-3"><StatusPill status={v.safety.status} /></td>
              <td className="py-2 pr-3 text-xs">{v.next_expiry ? `${v.next_expiry.title} · ${v.next_expiry.date}` : "—"}</td>
              <td className="py-2 pr-3"><StatusPill status={v.rental_readiness.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Fleet({ data, options }) {
  const [vehicle, setVehicle] = useState(null);
  const f = data.fleet;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cl-fleet-summary">
        <Stat label="Vehicles" value={f.total} />
        <Stat label="Legal ready" value={f.legal_ready} />
        <Stat label="Review required" value={f.review_required + f.not_configured} />
        <Stat label="Legally blocked" value={f.legally_blocked} tone={f.legally_blocked ? "text-rose-700" : "text-slate-900"} />
        <Stat label="LTO registration current (reviewed)" value={`${f.registration_current_verified} / ${f.total}`} />
        <Stat label="CTPL current (reviewed)" value={`${f.ctpl_current_verified} / ${f.total}`} />
        <Stat label="Rental-use insurance confirmed" value={`${f.rental_use_insurance_verified} / ${f.total}`} />
        <Stat label="Safety holds" value={f.safety_hold} />
      </div>
      <Section title="Fleet legal readiness" description={f.label}>
        <FleetTable vehicles={data.vehicles} onOpen={setVehicle} />
      </Section>
      {vehicle && (
        <Modal title="Vehicle legal & safety" onClose={() => setVehicle(null)} testId="cl-vehicle-modal">
          <VehicleLegalPanel vehicleId={vehicle} options={options} />
        </Modal>
      )}
    </div>
  );
}

function Insurance({ data, options }) {
  const [vehicle, setVehicle] = useState(null);
  return (
    <Section title="Vehicle insurance" description="Compulsory CTPL and rental-use cover are tracked separately. A private-use policy on a rented unit is a coverage review, never a finding that the policy is invalid.">
      {data.vehicles.length === 0 ? <Empty>No vehicles yet.</Empty> : (
        <ul className="space-y-2" data-testid="cl-insurance-list">
          {data.vehicles.map((v) => (
            <li key={v.vehicle_id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 p-3">
              <button type="button" className="text-sm font-medium underline" onClick={() => setVehicle(v.vehicle_id)}>{v.name}</button>
              <span className="flex flex-wrap gap-1">
                <TonePill tone="border-slate-200 bg-white text-slate-600">CTPL</TonePill>
                <StatusPill status={v.insurance_status || "NOT_VERIFIED"} />
                <TonePill tone="border-slate-200 bg-white text-slate-600">Rental use</TonePill>
                <StatusPill status={v.rental_use_cover || "NOT_VERIFIED"} />
              </span>
            </li>
          ))}
        </ul>
      )}
      {vehicle && (
        <Modal title="Vehicle legal & safety" onClose={() => setVehicle(null)}>
          <VehicleLegalPanel vehicleId={vehicle} options={options} />
        </Modal>
      )}
    </Section>
  );
}

function Locations({ data, options, caps, onSaved, onOpen }) {
  const [form, setForm] = useState({ name: "", location_type: "BRANCH", city: "", address: "" });
  const [busy, setBusy] = useState(false);
  const canEdit = hasCap(caps, "compliance.manage");
  const add = async () => {
    setBusy(true);
    try {
      await lrPost("/locations", form);
      toast.success("Location added. Its requirements were evaluated.");
      setForm({ name: "", location_type: "BRANCH", city: "", address: "" });
      onSaved();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      {data.locations.map((l) => (
        <Section key={l.location.id} title={l.location.name}
                 description={`${humanise(l.location.location_type)}${l.location.city ? ` · ${l.location.city}` : ""}`}
                 actions={<StatusPill status={l.status} />}>
          <p className="text-xs text-slate-500 mb-2">A permit for one location does not cover another.</p>
          <RequirementList rows={l.requirements.filter((r) => r.applicable)} onOpen={onOpen} testId={`cl-location-${l.location.id}`} />
        </Section>
      ))}
      {canEdit && options && (
        <Section title="Add a location">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Field label="Name"><input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   data-testid="cl-location-name" /></Field>
            <Field label="Type">
              <select className={inputClass} value={form.location_type} onChange={(e) => setForm({ ...form, location_type: e.target.value })}>
                {options.location_types.map((t) => <option key={t} value={t}>{humanise(t)}</option>)}
              </select>
            </Field>
            <Field label="City"><input className={inputClass} value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <Field label="Address"><input className={inputClass} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></Field>
          </div>
          <div className="mt-2 flex justify-end"><Button busy={busy} onClick={add} data-testid="cl-location-add">Add location</Button></div>
        </Section>
      )}
    </div>
  );
}

function Recurring({ data, onOpen }) {
  const recurring = [...data.requirements, ...data.locations.flatMap((l) => l.requirements)]
    .filter((r) => r.applicable && (r.has_expiry || r.category === "corporate"));
  return (
    <div className="space-y-3">
      <Section title="Recurring compliance" description="Renewals and review dates from your recorded documents. ConfirmPH does not invent statutory deadlines: enter the due dates you were given.">
        <RequirementList rows={recurring} onOpen={onOpen} testId="cl-recurring" />
      </Section>
      <Section title="Reminders" description="Sent at 60, 30, 14, 7 and 1 days and on expiry. These are reminders, not grace periods.">
        <ReminderList />
      </Section>
    </div>
  );
}

function ReminderList() {
  const [items, setItems] = useState(null);
  useEffect(() => { lrGet("/reminders").then((d) => setItems(d.items)).catch(() => setItems([])); }, []);
  if (!items) return <Loading />;
  if (!items.length) return <Empty>No reminders sent yet.</Empty>;
  return (
    <ul className="space-y-1 text-sm" data-testid="cl-reminders">
      {items.map((i) => <li key={i.id}>{i.title} · {i.milestone === 0 ? "expired" : `${i.milestone}-day reminder`} · {i.expiry_date}</li>)}
    </ul>
  );
}

function Documents({ caps }) {
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("");
  const [data, setData] = useState(null);
  const allowed = hasCap(caps, "compliance.documents.view");
  useEffect(() => {
    if (!allowed) return;
    lrGet("/evidence", { params: { page, page_size: 20, include_superseded: true, ...(category ? { category } : {}) } })
      .then(setData).catch((err) => toast.error(errorMessage(err)));
  }, [page, category, allowed]);
  if (!allowed) return <Section><Empty>You don't have access to compliance documents.</Empty></Section>;
  if (!data) return <Loading />;
  return (
    <Section title="Compliance documents" description="Private to your business. Opening a file is logged."
             actions={
               <select className={inputClass} value={category} onChange={(e) => { setPage(1); setCategory(e.target.value); }} aria-label="Category">
                 <option value="">All categories</option>
                 {["entity", "local", "bir", "transport", "fleet", "insurance", "privacy", "employment"].map((c) => <option key={c} value={c}>{humanise(c)}</option>)}
               </select>}>
      {data.items.length === 0 ? <Empty>No evidence uploaded yet.</Empty> : (
        <ul className="space-y-1 text-sm" data-testid="cl-documents">
          {data.items.map((e) => (
            <li key={e.id} className="flex flex-wrap justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
              <span>{humanise(e.requirement_key)} · v{e.version} · {humanise(e.document_type)}</span>
              <span className="text-xs text-slate-500">{e.status} · {e.uploaded_at?.slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex items-center justify-between text-xs">
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
        <span>{data.total} documents</span>
        <Button variant="secondary" disabled={page * 20 >= data.total} onClick={() => setPage(page + 1)}>Next</Button>
      </div>
    </Section>
  );
}

function HistoryTab() {
  const [items, setItems] = useState([]);
  const [next, setNext] = useState(undefined);
  const load = useCallback((before) => lrGet("/history", { params: { limit: 50, ...(before ? { before } : {}) } })
    .then((d) => { setItems((prev) => (before ? [...prev, ...d.items] : d.items)); setNext(d.next_before); })
    .catch((err) => toast.error(errorMessage(err))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <Section title="Compliance history" description="Nothing here is ever rewritten or deleted.">
      {items.length === 0 ? <Empty>No history yet.</Empty> : (
        <ol className="space-y-1 text-sm" data-testid="cl-history-tab">
          {items.map((h) => <li key={h.id}><span className="text-xs text-slate-500">{h.at?.slice(0, 16).replace("T", " ")}</span> · {h.summary} · {h.by_name}</li>)}
        </ol>
      )}
      {next && <div className="mt-2"><Button variant="secondary" onClick={() => load(next)}>Load older</Button></div>}
    </Section>
  );
}

export { FleetTable };
